import { createTheme, responsiveFontSizes } from '@material-ui/core';
import { palette } from './palette';

const CustomTheme = responsiveFontSizes(
  createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
    palette,
    layout: {
      contentWidth: 1600,
    },
    typography: {
      fontFamily: 'Poppins',
      h1: {
        letterSpacing: 0.75
      },
      h2: {
        fontSize: 55,
        letterSpacing: 0.75
      },
      h3: {
        letterSpacing: 0.75
      },
      h4: {
        letterSpacing: 0.75
      },
      h5: {
        letterSpacing: 0.75
      },
      h6: {
        letterSpacing: 0.75
      },
    },
    zIndex: {
      appBar: 1200,
      drawer: 1100,
    },
    customValues: {
      borderRadius: 10,
    },
    overrides: {
      MuiCssBaseline: {
        '@global': {
          html: {
            WebkitFontSmoothing: 'auto',
          },
          body: {
            fontFamily: [
              'VisbyCF',
              '-apple-system',
              'BlinkMacSystemFont',
              'Segoe UI',
              'Roboto',
              'Oxygen',
              'Ubuntu',
              'Cantarell',
              'Fira Sans',
              'Droid Sans',
              'Helvetica Neue',
              'sans-serif',
              'Poppins'
            ].join(', '),
          },
          a: {
            textDecoration: 'none',
            color: palette.primary.main,
          },
          '.bold': {
            fontWeight: 500,
          },
          '.semibold': {
            fontWeight: 400,
          },
          '.fullHeight': {
            height: '100%',
          },
          '.success': {
            color: `${palette.text.success} !important`,
          },
          '.recharts-legend-item': {
            marginTop: 15,
            marginBottom: 15,
          },
          ol: {
            paddingLeft: 32,
          },
          '.react-multiple-carousel__arrow--left': {
            left: 0
          },
          '.react-multiple-carousel__arrow--right': {
            right: 0
          },
          '.react-multiple-carousel__arrow--left::before': {
            content: '"\\2190"'
          },
          '.react-multiple-carousel__arrow--right::before': {
            content: '"\\2192"'
          },
          '.react-multiple-carousel__arrow': {
            background: palette.primary.main
          },
          '.react-multi-carousel-dot button': {
            border: 'none',
            height: 6,
            width: 35,
            borderRadius: 0,
            transition: 'all 0.3s',
            background: palette.text.disabled,

            '&:hover:active': {
              background: palette.primary.main,
            },
          },
          '.react-multi-carousel-dot--active button': {
            width: 70,
            backgroundColor: palette.secondary.main,
          },
          '.plan-selection-container .react-multi-carousel-dot button': {
            background: palette.primary.main,
            opacity: '0.2'
          },
          '.plan-selection-container .react-multi-carousel-dot--active button': {
            backgroundColor: palette.primary.main,
            opacity: '1'
          },
          '.hero-banner': {
            display: 'none',
          },
          '.planCard': {
            borderRadius: '12px !important',
          },
          '.wp-content': {
            fontSize: '0.93rem',
            lineHeight: 1.5,
            color: palette.text.primary,

            '& h6': {
              fontSize: 16,
              margin: '0 0 8px',
            },
            '& p': {
              margin: '20px 0',
            },
            '& li': {
              margin: '20px 0',

              '& li': {
                listStyleType: 'lower-roman'
              }
            },
            '& .heading': {
              fontSize: '2rem',
              margin: '40px 0 0',
            },
            '& .subHeading': {
              fontSize: '1.5rem',
              margin: '35px 0 0',
            },
            '& .sectionHeading': {
              fontSize: '1.25rem',
              margin: '35px 0 0',
            },
          },
        },
      },
      MuiAccordionSummary: {
        expandIcon: {
          '& .collapseIcon': {
            display: 'none',
          },
          '&$expanded': {
            '& .collapseIcon': {
              display: 'block',
            },
            '& .expandIcon': {
              display: 'none',
            },
          },
          '.mainContainer & svg': {
            color: `${palette.icon.tertiary} !important`,
          },
        },
      },
      MuiAlert: {
        outlinedSuccess: {
          background: palette.background.success,
        },
        outlinedError: {
          background: palette.background.error,
        },
      },
      MuiButton: {
        root: {
          fontWeight: 500,
          textTransform: 'none',
          letterSpacing: 0.75,
          padding: 13,
          boxShadow: 'none !important',
          borderRadius: 80,

          '&.pill': {
            borderRadius: 30,
          },

          '&.primary-contrast': {
            background: palette.primary.contrastText,
            color: palette.text.primary,
          },
        },
        outlined: {
          padding: 13,
          borderWidth: 2,
          color: palette.text.primary,
          borderColor: palette.primary.main,
          '&.timeSlot': {
            borderWidth: 1,
            borderColor: `${palette.border.primary} !important`,
            color: `${palette.text.normal} !important`,

            '&:hover': {
              backgroundColor: `${palette.primary.main} !important`,
              borderColor: `${palette.primary.main} !important`,
              color: `${palette.text.highlight} !important`,
            },
          },
        },
        contained: {
          '&.timeSlot': {
            backgroundColor: `${palette.primary.main} !important`,
            borderColor: `${palette.primary.main} !important`,
            color: `${palette.text.highlight} !important`,
          },
        },
        containedSecondary: {
          color: palette.text.primary
        },
        outlinedSecondary: {
          color: palette.text.primary,
          borderColor: palette.text.highlight,
          background: palette.text.highlight,
          '&:hover': {
            borderColor: palette.verticalBar.used,
            backgroundColor: palette.verticalBar.used,
            color: palette.text.highlight
          }
        },
        containedPrimary: {
          color: palette.primary.contrastText,
          backgroundColor: palette.primary.main,
          '&:hover': {
            color: palette.text.highlight,
            backgroundColor: `${palette.button.hover} !important`,
          },
          '[role="dialog"]:not(.gm-style-iw) &': {
            padding: '16px 20px',
            fontSize: '18px !important',
            fontWeight: 400,
            lineHeight: '20px',
            letterSpacing: '-0.36px',

            '&:not(.Mui-disabled)': {
              backgroundColor: `${palette.primary.main} !important`,
              color: `${palette.text.highlight} !important`,

              '&:hover': {
                backgroundColor: `${palette.button.hover} !important`,
              },
            },
          },
        },
        textPrimary: {
          color: palette.brand.main,
          '[role="dialog"]:not(.gm-style-iw) &': {
            color: `${palette.brand.main} !important`,
            fontSize: '18px !important',
            fontWeight: 400,
            letterSpacing: '-0.36px',
          },
        },
        outlinedPrimary: {
          color: palette.text.primary,
          '&:hover': {
            borderColor: palette.verticalBar.used,
            backgroundColor: palette.verticalBar.used,
            color: palette.text.highlight
          }
        },
        startIcon: {
          marginLeft: 0,
        },
      },
      MuiDrawer: {
        paper: {
          width: 450,
          maxWidth: '100%',
        },
      },
      MuiIconButton: {
        root: {
          '&.slot-calendar-button': {
            width: 56,
            height: 56,
            padding: 16,
            borderRadius: 9999,
            border: `1px solid ${palette.border.brand}`,
            backgroundColor: palette.background.tertiary,
            backgroundImage: 'url(/images/calendar-icon.svg)',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundSize: '24px 24px',
          },
        },
        colorPrimary: {
          color: palette.primary.main,
        },
        colorSecondary: {
          backgroundColor: palette.background.paper,

          '&:hover': {
            backgroundColor: palette.background.level2,
          },
        },
      },
      MuiButtonGroup: {
        root: {
          border: `1px solid ${palette.primary.main}`,
          padding: 4,
          borderRadius: 20,
        },
        grouped: {
          borderRadius: 20,
          fontSize: '0.7rem',
        },
        groupedHorizontal: {
          '&:not(:first-child)': {
            borderTopLeftRadius: 20,
            borderBottomLeftRadius: 20,
          },
          '&:not(:last-child)': {
            borderTopRightRadius: 20,
            borderBottomRightRadius: 20,
          },
        },
        groupedTextHorizontal: {
          '&:not(:last-child)': {
            borderRight: 0,
          },
        },
      },
      MuiInputBase: {
        input: {
          scrollMargin: '100px',
        },
      },
      MuiInputAdornment: {
        positionEnd: {
          height: '100%',
          position: 'relative',
          background: palette.background.paper,
        },
      },
      MuiOutlinedInput: {
        root: {
          borderRadius: 8,
          '.mainContainer &.Mui-focused fieldset': {
            borderColor: `${palette.brand.main} !important`,
          },
          '&:has(.select-custom-field)': {
            '& fieldset': {
              borderColor: `${palette.border.subtle} !important`,
              borderWidth: '1px !important',
            },
            '&.Mui-focused fieldset': {
              borderColor: `${palette.brand.main} !important`,
              borderWidth: '1px !important',
            },
          },
        },
      },
      MuiPaper: {
        root: {
          '&.widget': {
            padding: 24,
          },
          '&.cardRoot': {
            borderColor: palette.border.primary,
            borderRadius: 8,
          },
        },
        rounded: {
          borderRadius: 10,
        },
      },
      MuiGrid: {
        root: {
          '&.detail-card-service-icon': {
            backgroundColor: palette.background.light,

            '& img': {
              filter: 'brightness(0) saturate(100%) invert(48%) sepia(20%) saturate(731%) hue-rotate(54deg) brightness(96%) contrast(92%)',
            },
          },
        },
      },
      MuiTextField: {
        root: {
          '&.address-suggestion-text-field:has(.proximityIcon)': {
            '& fieldset': {
              border: 'none !important',
            },
            '& .address-suggestion-input-field': {
              paddingRight: '0 !important',
            },
            '& .address-suggestion-input-field input::placeholder': {
              color: `${palette.text.tertiary} !important`,
              opacity: '1 !important',
              letterSpacing: '-0.28px',
            },
            '& .address-suggestion-text-field-label': {
              position: 'absolute',
              width: 1,
              height: 1,
              padding: 0,
              margin: -1,
              border: 0,
              overflow: 'hidden',
              clip: 'rect(0 0 0 0)',
              whiteSpace: 'nowrap',
            },
          },
        },
      },
      MuiCardContent: {
        root: {
          padding: 24,
          '.cardRoot & > svg': {
            fontSize: '30px !important',
            color: `${palette.icon.tertiary} !important`,
          },
        },
      },
      MuiSelect: {
        nativeInput: {
          scrollMargin: '150px',
        },
      },
      MuiSnackbar: {
        anchorOriginTopCenter: {
          top: '85px !important',
        },
      },
      MuiTab: {
        root: {
          '&.compact': {
            padding: '12px 16px'
          },
        },
      },
      MuiTabs: {
        root: {
          '&.subscription-tabs': {
            '& .MuiTab-wrapper': {
              textTransform: 'none',
            },
            '& .MuiTabs-scroller': {
              display: 'flex',
              justifyContent: 'center',
            },
            '& .MuiTabs-flexContainer': {
              background: palette.primary.contrastText,
              borderRadius: '2rem',

              '& .MuiTab-root': {
              },
              '& .Mui-selected': {
                background: palette.secondary.main,
                color: palette.secondary.contrastText,
                borderRadius: '2rem'
              }
            },
            '& .MuiTabs-indicator': {
              display: 'none'
            }
          }
        },
        vertical: {
          '& .MuiTab-root': {
            paddingLeft: 20,
          },
          '& .MuiTab-wrapper': {
            alignItems: 'flex-start',
          },
        },
      },
      MuiTabScrollButton: {
        root: {
          width: 25,
          '&$disabled': {
            width: 0,
            transition: 'all 0.2s',
          },
        },
      },
      PrivateTabIndicator: {
        vertical: {
          left: 0,
        },
        root: {
          height: 3,
        },
      },
      MuiTableRow: {
        root: {
          '&:nth-of-type(even)': {
            background: palette.background.tableAlternate,
          },
        },
      },
      MuiTableCell: {
        root: {
          borderBottom: 0,
          background: palette.text.highlight,
          color: palette.text.highlight,
        },
        head: {
          background: palette.text.highlight,
          padding: '12px 16px',
        },
      },
      MUIDataTableHeadCell: {
        fixedHeader: {
          backgroundColor: palette.background.tableHead,
        },
        data: {
          fontSize: '0.875rem',
          fontWeight: 500,
        },
      },
      MuiTableSortLabel: {
        root: {
          marginTop: 2,
        },
      },
      MuiTypography: {
        body1: {
          '@media(max-width: 600px)': {
            fontSize: '0.9rem',
          },
        },
        root: {
          '.radio-card &': {
            fontSize: '18px !important',
            lineHeight: '24px !important',
            letterSpacing: '-0.36px',
            color: `${palette.text.normal} !important`,
          },
          '.radio-card.radio-card-selected &': {
            color: `${palette.brand.main} !important`,
          },
        },
        h4: {
          '.mainContainer &': {
            fontSize: 32,
            fontWeight: 500,
            lineHeight: '36px',
            letterSpacing: '-1.28px',
            color: palette.text.heading,
          },
        },
        h5: {
          '.detail-card &': {
            fontSize: 18,
            fontWeight: 400,
            lineHeight: '24px',
            letterSpacing: '-0.36px',
            color: palette.text.heading,
          },
        },
        colorTextPrimary: {
          '.cardRoot &': {
            color: palette.text.heading,
          },
          '.cardRoot:hover &': {
            color: `${palette.text.heading} !important`,
          },
        },
      },
      MuiDivider: {
        root: {
          '.detail-card &': {
            backgroundColor: palette.border.card,
          },
        },
      },
      MuiSvgIcon: {
        root: {
          '.slot-calendar-button &': {
            display: 'none',
          },
          '.proximityIcon &': {
            fontSize: '20px !important',
            color: `${palette.icon.brand} !important`,
          },
        },
      },
      MuiCheckbox: {
        root: {
          '.mainContainer &.Mui-checked': {
            color: `${palette.primary.main} !important`,
          },
        },
      },
      MuiRadio: {
        root: {
          '.radio-card &': {
            '& > span:first-child': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxSizing: 'border-box',
              width: '20px !important',
              height: '20px !important',
              borderRadius: '50%',
              border: `1px solid ${palette.border.subtle}`,
            },
            '& svg': {
              display: 'none !important',
            },
          },
          '.radio-card &.Mui-checked > span:first-child': {
            backgroundColor: `${palette.brand.main} !important`,
            borderColor: `${palette.brand.main} !important`,

            '&::after': {
              content: '""',
              display: 'block',
              width: 7.5,
              height: 7.5,
              borderRadius: '50%',
              backgroundColor: palette.text.highlight,
            },
          },
        },
      },
      MuiFormLabel: {
        root: {
          '.mainContainer &.Mui-focused': {
            color: `${palette.brand.main} !important`,
          },
        },
      },
      MuiPickersDay: {
        current: {
          color: palette.text.primary
        },
        daySelected: {
          backgroundColor: palette.background.neutral,
          color: palette.text.highlight,

          '&:hover': {
            backgroundColor: palette.background.neutral,
          }
        }
      },
      MuiLinearProgress: {
        barColorSecondary: {
          backgroundColor: palette.verticalBar.unlimited
        }
      },
    },
  }),
);

export default CustomTheme;
